import { NodeRenderer } from '@contentful/rich-text-react-renderer'
import { useTranslation } from 'next-i18next'

import { INLINES } from '@src/quince-site-common/src/constants/contentfulRichTextTypes'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { ZipCodeButton } from '../../atoms'

const CountdownElement = ({
  innerText,
  classNames,
  endTimeEpoch,
}: {
  innerText?: string
  classNames: string[]
  endTimeEpoch?: number
}) => {
  if (!innerText) return null
  const END_OF_TIME = /end-time-epoch--\w+/g
  const [, endOfTime] = classNames.find(className => className.match(END_OF_TIME))?.split('--') || []
  return (
    <count-down end-time-epoch={endTimeEpoch ? endTimeEpoch.toString() : endOfTime} template={innerText}></count-down>
  )
}

const FlipWordElement = ({ innerText }: { innerText?: string; classNames: string[] }) => {
  if (!innerText) return null
  const wordsToFlip = innerText.split(' ')

  return (
    <span className="flipWordsWrapper">
      {wordsToFlip.map((wordToFlip, index) => (
        <>
          <span className="flipWord">{wordToFlip}</span>
          {index < wordsToFlip.length - 1 && <span className="flipWordSpace"> </span>}
        </>
      ))}
    </span>
  )
}

const ZipCodeElement = ({ innerText }: { innerText?: string; classNames: string[] }) => {
  const { t } = useTranslation('common')
  const { zipCode } = useZipCode()
  if (!innerText) return null

  const buttonText = zipCode ? zipCode : t('enter_your_zip')
  const [firstString = '', secondString = ''] = innerText.split('{{ZIP}}')
  return (
    <>
      {firstString}
      {zipCode ? t('to') : '. '}
      <ZipCodeButton>{buttonText}</ZipCodeButton>
      {secondString}
    </>
  )
}

enum ENTRY_TYPES {
  countdown = 'countdown',
  flip_word = 'flip_word',
  zip_code = 'zip_code',
}

const ENTRY_TYPE_VS_RENDERER = {
  [ENTRY_TYPES.countdown]: CountdownElement,
  [ENTRY_TYPES.flip_word]: FlipWordElement,
  [ENTRY_TYPES.zip_code]: ZipCodeElement,
}

export const getEndTimeEpoch = (endTime?: string): number => {
  if (!endTime) return 0
  const endTimeEpoch = Date.parse(endTime)
  if (Number.isNaN(endTimeEpoch)) {
    console.error(`Invalid endTime provided to isTimerExpired: "${endTime}"`)
    return 0
  }
  return endTimeEpoch
}
// Renderer for `entry-hyperlink` inlines.
//
// The Contentful renderer's default expects `node.data.target.sys.id`, but
// the shape we receive from PL/BFF is flattened (`data.target = { id, type,
// linkType, slug? }`, no `sys` envelope, no `fields`, and no rich-text
// `references` array). Touching `target.sys.id` on that shape used to throw
// and — on PDP, which had no per-section ErrorBoundary — whitescreened the
// page (CLP silently drops the widget via `PLBaseErrorBoundary`).
//
// When the BFF includes `target.slug` (a path like `/women/cashmere`), we
// render an internal `<Link>` so navigation works client-side. When the slug
// is missing or empty, we fall back to a non-clickable `<span>` carrying the
// entry id — the link text still renders inline, and we never throw.
function normalizeSlug(rawSlug: unknown): string | null {
  if (typeof rawSlug !== 'string') return null
  const trimmed = rawSlug.trim()
  if (!trimmed) return null
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

const renderEntryHyperlink: NodeRenderer = (node, children) => {
  const target = node.data?.target
  const id = target?.id
  const href = normalizeSlug(target?.slug)

  if (href) {
    return <a href={href}>{children}</a>
  }
  return <span data-entry-id={id}>{children}</span>
}
export const options = {
  renderNode: {
    [INLINES.ENTRY_HYPERLINK]: renderEntryHyperlink,
    [INLINES.EMBEDDED_ENTRY]: (element: any) => {
      if (element?.nodeType === 'embedded-entry-inline') {
        const embeddedEntryData = element?.data?.target

        const { baseDynamicValueClasses, content } = embeddedEntryData
        const classNames: string[] = baseDynamicValueClasses || []
        const innerText = content?.content?.[0]?.content
          ?.map((c: { value: string }) => c.value)
          .join('')
          .trim()

        const ENTRY_TYPE = /type--\w+/g

        const [, entryType] = classNames.find(className => className.match(ENTRY_TYPE))?.split('--') || []

        const endTimeEpoch = getEndTimeEpoch(embeddedEntryData?.endTime)
        const RenderElement = ENTRY_TYPE_VS_RENDERER[entryType as ENTRY_TYPES]
        if (RenderElement) {
          return <RenderElement innerText={innerText} classNames={classNames} endTimeEpoch={endTimeEpoch} />
        } else {
          const error = new Error(`Unhandled EMBEDDED_ENTRY type used. used ${entryType}`)
          // @i18n-ignore
          // eslint-disable-next-line i18next/no-literal-string
          error.name = 'Unhandled EMBEDDED_ENTRY type'

          captureException(error, {
            module: MODULES.UNKNOWN,
            feature: FEATURES.UNHANDLED_RICHTEXT_EMBEDDED_ENTRY,
            extraInfo: {
              entryType,
            },
          })
        }
      }
      return null
    },
  },
}
